/**
 * Single source of truth for everything SEO / structured data needs to know
 * about the site. Keep this in sync with the imprint and privacy policy.
 */

/** Canonical origin. No trailing slash. */
export const SITE_URL = "https://www.skinuvita.de"

export const SITE_NAME = "Skinuvita"

/** The only locale we want indexed; every other locale is served noindex. */
export const DEFAULT_LOCALE = "de"

export const DEFAULT_OG_IMAGE = `${SITE_URL}/og/skinuvita-og.jpg`
export const DEFAULT_OG_IMAGE_WIDTH = 1200
export const DEFAULT_OG_IMAGE_HEIGHT = 630

export const ORGANIZATION = {
  legalName: "Skinuvita GmbH",
  name: SITE_NAME,
  alternateName: "Skinuvita GmbH",
  email: "info@skinuvita.de",
  supportEmail: "support@skinuvita.de",
  telephone: "+49 421 49915300",
  /** Business address per the Impressum (§ 5 TMG). */
  street: "c/o Clockwise Bremen, Martinistraße 62-66",
  postalCode: "28195",
  city: "Bremen",
  country: "DE",
  logo: `${SITE_URL}/images/skinuvita_logo.png`,
  vatId: "DE347779236",
  registerCourt: "Amtsgericht Bremen",
  registerNumber: "HRB 39999",
  /** Handelsregister entry for HRB 39999; the company launched in September 2021. */
  foundingDate: "2021",
  founders: ["Jan B. Elsner", "Maria Araujo", "Till Fitzke", "Jens Pelzetter", "Janina Clasen"],
  award: "Bremer Gründungspreis 2024",
  slogan: "UVB-Lichttherapie für Zuhause",
  /** Topics the site actually has substantive content about. */
  knowsAbout: [
    "UVB-Lichttherapie",
    "Schmalband-UVB (311 nm)",
    "Phototherapie",
    "Psoriasis (Schuppenflechte)",
    "Atopische Dermatitis (Neurodermitis)",
    "Hand- und Fußekzeme",
    "Chronisch-entzündliche Hauterkrankungen",
    "Heimtherapie mit ärztlicher Begleitung",
    "Digitale Gesundheitsanwendungen",
  ],
  sameAs: ["https://www.instagram.com/skinuvita", "https://www.linkedin.com/company/skinuvita"],
} as const

/**
 * Turns a router path into an absolute canonical URL.
 *
 * - drops the query string and hash (they never identify a distinct document here)
 * - drops the trailing slash on everything but the homepage, matching how Next
 *   serves this site (`trailingSlash` is off)
 * - prefixes non-default locales, so /en/ratgeber canonicalises to itself rather
 *   than to the German page
 */
export function buildCanonicalUrl(asPath: string, locale?: string): string {
  const pathOnly = (asPath || "/").split("#")[0].split("?")[0]
  const localePrefix = locale && locale !== DEFAULT_LOCALE ? `/${locale}` : ""
  const path = `${localePrefix}${pathOnly}`

  if (path === "" || path === "/") {
    return `${SITE_URL}/`
  }

  return `${SITE_URL}${path.replace(/\/+$/, "")}`
}
